// =========================================================================
// RouteCards — push a root-cause finding to the people who act on it.
// Issues module, fork-owned (zone C).
//
// Shared by the alert page's routing panel and the Routing & training scene.
// Exports:
//
//   <CausePill>        the verdict chip — Operator / Equipment / Mixed / Unclear
//   <RouteCards>       one card per person, grouped Operations | Maintenance,
//                      each with the asks that role owns and one primary button
//   useRouteSends()    the "sent" bookkeeping: a local Set plus the toast
//   <RosterNote>       the synthesised-roster provenance line, opening the
//                      shift rows it was split from
//   useAlertCauses()   root cause per open alert, for the cause pills
//   <RoutingLinkCard>  the Issues Live way in to /issues/routing
//
// Sending is local to the page: the button raises a toast and flips the card
// to its done label. Nothing is persisted — a reload puts every card back, as
// the alert page's Approve buttons do.
//
// UI layer. Joins, grouping and button copy come from the sibling transform.
//
// @see docs/logic/issues/alert-routing.md
// =========================================================================

import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Package, Send, Waypoints } from 'lucide-react';
import { cn } from '@/shared/cn';
import type { RootCause, RoutingDecision } from '@/versions/v1/schema/operations';
import { rootCause, routingDecisions, trainingNeeds } from '@/versions/v1/shared/reliability';
import { useReliability } from '@/versions/v1/shared/reliability/useReliability';
import { ALERTS } from '@/versions/v1/shared/seed/alerts';
import { useToast } from '@/versions/v1/shared/toast/ToastContext';
import {
  CAUSE_META,
  causeSummary,
  decisionKey,
  pct,
  routeGroups,
  sentKey,
  stockLine,
  type AlertCause,
  type RouteCardModel,
} from './RouteCards.transform';
import { useSourceDrawer } from './useSourceDrawer';

// ---------------------------------------------------------------------------
// Cause pill
// ---------------------------------------------------------------------------

export function CausePill({
  cause,
  confidence,
  size = 'sm',
  long = false,
}: {
  cause: RootCause;
  confidence?: number;
  size?: 'xs' | 'sm' | 'md';
  /** Long label ("Operator behaviour") instead of the short one. */
  long?: boolean;
}) {
  const meta = CAUSE_META[cause];
  return (
    <span
      title={meta.means}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full font-medium',
        size === 'xs' && 'px-1.5 py-0.5 text-[10px]',
        size === 'sm' && 'px-2 py-0.5 text-[11px]',
        size === 'md' && 'px-2.5 py-1 text-xs',
      )}
      style={{ background: meta.soft, color: meta.color }}
    >
      <i aria-hidden className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: meta.color }} />
      {long ? meta.label : meta.short}
      {confidence === undefined ? null : (
        <span className="mono font-normal opacity-80">{pct(confidence)}</span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Sent bookkeeping
// ---------------------------------------------------------------------------

export interface RouteSends {
  isSent: (decision: string, card: string) => boolean;
  send: (decision: string, card: RouteCardModel) => void;
}

/** Local "sent" state for route cards plus the toast. */
export function useRouteSends(): RouteSends {
  const { show } = useToast();
  const [sent, setSent] = useState<ReadonlySet<string>>(() => new Set());
  const isSent = useCallback((decision: string, card: string) => sent.has(sentKey(decision, card)), [sent]);
  const send = useCallback(
    (decision: string, card: RouteCardModel) => {
      setSent((prev) => new Set(prev).add(sentKey(decision, card.key)));
      show(card.primary.toast);
    },
    [show],
  );
  return { isSent, send };
}

// ---------------------------------------------------------------------------
// Route cards
// ---------------------------------------------------------------------------

const SIDE_COLOR = { operations: 'var(--info)', maintenance: 'var(--warn)' } as const;

export function RouteCards({
  decision,
  sends,
  className,
}: {
  decision: RoutingDecision;
  sends: RouteSends;
  className?: string;
}) {
  const groups = routeGroups(decision);
  const dKey = decisionKey(decision);
  const split = groups.length > 1;

  return (
    <div className={cn('grid gap-3', split && 'lg:grid-cols-2', className)}>
      {groups.map((group) => (
        <section key={group.side} aria-label={`${group.label} routes`} className="min-w-0">
          <header className="mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em]"
              style={{ color: SIDE_COLOR[group.side] }}
            >
              <i aria-hidden className="inline-block h-2 w-2 rounded-sm" style={{ background: SIDE_COLOR[group.side] }} />
              {group.label}
            </span>
            <span className="text-[11px] text-[var(--ink-tertiary)]">{group.blurb}</span>
          </header>
          <div className={cn('grid gap-2', !split && 'md:grid-cols-2 xl:grid-cols-3')}>
            {group.cards.map((card) => (
              <RouteCard
                key={card.key}
                card={card}
                sent={sends.isSent(dKey, card.key)}
                onSend={() => sends.send(dKey, card)}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function RouteCard({ card, sent, onSend }: { card: RouteCardModel; sent: boolean; onSend: () => void }) {
  const color = SIDE_COLOR[card.side];
  return (
    <article
      className="flex min-w-0 flex-col gap-2 rounded-lg border border-[var(--hairline)] bg-[var(--surface-1)] p-3"
      style={{ boxShadow: `inset 3px 0 0 ${color}` }}
    >
      <div className="flex items-start gap-2.5">
        <span
          aria-hidden
          className="mono grid h-8 w-8 flex-none place-items-center rounded-full text-[11px] font-semibold"
          style={{ background: card.side === 'operations' ? 'var(--info-soft)' : 'var(--warn-soft)', color }}
        >
          {card.initials}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
            <b className="text-[13px] font-semibold text-[var(--ink-primary)]">{card.personName}</b>
            {card.synthesised ? (
              <span
                title="Proof-of-concept stand-in — this role holder is not in the site directory"
                className="rounded border border-dashed border-[var(--hairline-strong)] px-1 text-[9.5px] uppercase tracking-[0.05em] text-[var(--ink-tertiary)]"
              >
                stand-in
              </span>
            ) : null}
          </div>
          <div className="text-[11px] text-[var(--ink-tertiary)]">
            {card.role} · {card.mine}
          </div>
        </div>
        <span className="shrink-0 rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-[10.5px] font-medium text-[var(--ink-secondary)]">
          {card.channel}
        </span>
      </div>

      <p className="text-[11.5px] leading-relaxed text-[var(--ink-secondary)]">{card.why}</p>

      {card.actions.length ? (
        <ul className="space-y-1 border-t border-[var(--hairline)] pt-2">
          {card.actions.map((a) => (
            <li key={`${a.kind}-${a.text}`} className="flex gap-1.5 text-[11px] leading-snug text-[var(--ink-secondary)]">
              <span aria-hidden className="mt-[5px] inline-block h-1 w-1 flex-none rounded-full bg-[var(--ink-tertiary)]" />
              <span className="min-w-0">{a.text}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="mt-auto flex flex-wrap items-center gap-2 pt-1">
        {card.part ? (
          <span className="inline-flex min-w-0 items-center gap-1.5 text-[10.5px] text-[var(--ink-tertiary)]">
            <Package aria-hidden className="h-3.5 w-3.5 flex-none" />
            <span className="mono font-medium text-[var(--ink-primary)]">{card.part.partNo}</span>
            <span className="truncate">{stockLine(card.part, card.mine)}</span>
          </span>
        ) : null}
        <button
          type="button"
          onClick={onSend}
          disabled={sent}
          aria-label={sent ? `${card.primary.doneLabel} — ${card.personName}` : `${card.primary.label} to ${card.personName}`}
          className={cn(
            'ml-auto inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-medium transition-opacity focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]',
            sent
              ? 'cursor-default bg-[var(--good-soft)] text-[var(--good)]'
              : 'bg-[var(--accent)] text-[var(--accent-text)] hover:opacity-90',
          )}
        >
          {sent ? <Check aria-hidden className="h-3.5 w-3.5" /> : <Send aria-hidden className="h-3.5 w-3.5" />}
          {sent ? card.primary.doneLabel : card.primary.label}
        </button>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Alert causes + the Issues Live link card
// ---------------------------------------------------------------------------

/**
 * Root cause for each open alert, keyed by alert id — `null` until the
 * session's reliability context has loaded. One `rootCause` call per alert.
 */
export function useAlertCauses(): ReadonlyMap<string, AlertCause> | null {
  const { ctx } = useReliability();
  return useMemo(() => {
    if (!ctx) return null;
    return new Map(
      ALERTS.map((a) => {
        const f = rootCause(a.machine, a.comp, ctx);
        return [a.id, { cause: f.cause, confidence: f.confidence }] as const;
      }),
    );
  }, [ctx]);
}

/**
 * The Issues Live way in to /issues/routing: the open alerts' cause split,
 * the operator-side calls beyond the alerts, and the coaching count. Renders
 * a quiet skeleton line while the context loads; nothing on failure (the
 * page it sits on does not depend on it).
 */
export function RoutingLinkCard() {
  const { ctx, error } = useReliability();
  const causes = useAlertCauses();
  const extra = useMemo(() => {
    if (!ctx) return null;
    const flaggedOperatorSide = routingDecisions(ctx).filter(
      (d) => d.source === 'flagged' && (d.cause === 'operator' || d.cause === 'mixed'),
    ).length;
    return { flaggedOperatorSide, training: trainingNeeds(ctx.attribution).length };
  }, [ctx]);

  if (error) return null;

  return (
    <Link
      to="/issues/routing"
      className="group flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-[var(--hairline)] bg-[var(--surface-1)] px-4 py-3 transition-colors hover:border-[var(--accent)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
    >
      <span className="grid h-8 w-8 flex-none place-items-center rounded-lg bg-[var(--info-soft)] text-[var(--info)]">
        <Waypoints aria-hidden className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold text-[var(--ink-primary)]">
          Operator or equipment — and who acts
        </span>
        {causes && extra ? (
          <span className="block text-[11.5px] text-[var(--ink-tertiary)]">
            Open alerts: {causeSummary(causes.values())} · {extra.flaggedOperatorSide} more operator-side call
            {extra.flaggedOperatorSide === 1 ? '' : 's'} beyond the alerts · {extra.training} operators flagged for
            coaching
          </span>
        ) : (
          <span className="mt-1 block h-3 w-72 max-w-full animate-pulse rounded bg-[var(--surface-3)]" />
        )}
      </span>
      <span className="inline-flex items-center gap-1 text-[11.5px] font-medium text-[var(--accent)] group-hover:underline">
        Routing &amp; training <ArrowRight aria-hidden className="h-3.5 w-3.5" />
      </span>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Provenance
// ---------------------------------------------------------------------------

/**
 * The synthesised-roster line. The data pack carries machine-day hours and
 * alarm counts; the day/night split, the operator on each shift and most
 * role holders are proof-of-concept seed. The link opens the source rows the
 * split was made from, with the rule in the calc box.
 */
export function RosterNote({ machine, className }: { machine?: string; className?: string }) {
  const { openDataset, pending } = useSourceDrawer();
  return (
    <p className={cn('text-[10.5px] leading-relaxed text-[var(--ink-tertiary)]', className)}>
      <span className="font-medium text-[var(--ink-secondary)]">Synthesised roster.</span> Machine-day hours and alarm
      counts are from the data pack; the day/night split, who drove each shift and the stand-in role holders are
      seeded for this proof of concept.{' '}
      <button
        type="button"
        aria-busy={pending === 'shift'}
        onClick={() =>
          openDataset('shift', {
            title: machine ? `Shift records · ${machine}` : 'Shift records',
            ...(machine ? { filter: { machine_id: machine } } : {}),
            calc: {
              formula:
                'Each machine-day is split into a day and a night shift; each shift is credited to the crew rostered on and that crew’s operator for the machine. Events land on a shift in proportion to its hours unless a story override concentrates them. Splits sum back exactly to these rows.',
              steps: [
                'Source: shift_daily (hours, abuse) and events_daily (alarm counts) — machine × day, no shift, no operator',
                'Synthesised: roster pattern per site, operator per crew, 12 % seeded machine swaps, day/night hours split',
                'Check: check:reliability reconciles every row to 0.1 h and every count exactly',
              ],
            },
          })
        }
        className="font-medium text-[var(--accent)] underline decoration-dotted underline-offset-2 hover:no-underline"
      >
        source rows ›
      </button>
    </p>
  );
}
